import { useRef, useCallback } from 'react';

interface ImageLoadMetrics {
  src: string;
  loadTime: number;
  success: boolean;
  timestamp: number;
}

export const useImagePerformance = () => {
  const startTimes = useRef<Map<string, number>>(new Map());
  const metrics = useRef<ImageLoadMetrics[]>([]);

  const trackImageStart = useCallback((src: string) => {
    startTimes.current.set(src, performance.now());
  }, []);

  const trackImageLoad = useCallback((src: string, success: boolean) => {
    const startTime = startTimes.current.get(src);
    if (startTime) {
      const loadTime = performance.now() - startTime;
      const metric: ImageLoadMetrics = {
        src,
        loadTime,
        success,
        timestamp: Date.now()
      };
      
      metrics.current.push(metric);
      startTimes.current.delete(src);

      // Log slow loading images (> 2 seconds)
      if (loadTime > 2000) {
        console.warn(`Slow image loading detected: ${src} took ${Math.round(loadTime)}ms`);
      }

      // Keep only last 50 metrics to prevent memory leaks
      if (metrics.current.length > 50) {
        metrics.current = metrics.current.slice(-50);
      }
    }
  }, []);

  const getMetrics = useCallback(() => {
    return {
      totalImages: metrics.current.length,
      averageLoadTime: metrics.current.length > 0 
        ? metrics.current.reduce((sum, m) => sum + m.loadTime, 0) / metrics.current.length 
        : 0,
      successRate: metrics.current.length > 0 
        ? (metrics.current.filter(m => m.success).length / metrics.current.length) * 100 
        : 0,
      slowImages: metrics.current.filter(m => m.loadTime > 2000).length
    };
  }, []);

  return {
    trackImageStart,
    trackImageLoad,
    getMetrics
  };
};
